import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ExternalLink, Loader2 } from "lucide-react";
import {
  adminCreate, adminGet, adminUpdate, fetchCategories, fetchProducts, formatApiError,
} from "../../lib/api";
import { slugify } from "../../components/TableOfContents";
import { ArticleForm, ComparisonForm, ProductForm, CategoryForm } from "../../components/admin/ContentForms";
import { KINDS, cleanPayload } from "./adminConfig";

const FORMS = { articles: ArticleForm, comparisons: ComparisonForm, products: ProductForm, categories: CategoryForm };

export default function AdminEdit() {
  const { kind, id } = useParams();
  const cfg = KINDS[kind];
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [slugTouched, setSlugTouched] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    if (!cfg) return;
    setData(null);
    setSlugTouched(!!id);
    if (id) adminGet(cfg.api, id).then(setData).catch((e) => toast.error(formatApiError(e)));
    else setData(cfg.defaults());
  }, [cfg, id]);

  useEffect(() => {
    fetchCategories().then((list) => setCategories(list.map((c) => ({ value: c.name, label: c.name }))));
    fetchProducts().then((list) => setProducts(list.map((p) => ({ value: p.slug, label: p.name }))));
  }, []);

  if (!cfg) return <Navigate to="/admin/reviews" replace />;

  const update = (key, value) =>
    setData((d) => {
      const next = { ...d, [key]: value };
      if (key === cfg.titleKey && !slugTouched) next.slug = slugify(value);
      return next;
    });

  const onSlugChange = (v) => {
    setSlugTouched(true);
    setData((d) => ({ ...d, slug: slugify(v) }));
  };

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = cleanPayload(data);
      if (id) await adminUpdate(cfg.api, id, payload);
      else await adminCreate(cfg.api, payload);
      toast.success(id ? "Changes saved" : `${cfg.singular[0].toUpperCase()}${cfg.singular.slice(1)} published`);
      navigate(`/admin/${kind}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!data)
    return <div className="text-neutral-500 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading…</div>;

  const Form = FORMS[cfg.api];

  return (
    <form onSubmit={onSave} className="max-w-6xl" data-testid="admin-edit-form">
      <Link to={`/admin/${kind}`} className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-indigo-800 transition-colors mb-5" data-testid="admin-edit-back">
        <ArrowLeft size={14} /> All {cfg.label.toLowerCase()}
      </Link>
      <h1 className="font-playfair text-3xl sm:text-4xl text-neutral-900 tracking-tight mb-8 line-clamp-2" data-testid="admin-edit-heading">
        {id ? data[cfg.titleKey] || "Untitled" : `New ${cfg.singular}`}
      </h1>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_280px] gap-8 items-start">
        <Form data={data} update={update} onSlugChange={onSlugChange} categories={categories} products={products} cfg={cfg} />

        <aside className="xl:sticky xl:top-8 bg-surface border border-neutral-200 rounded-xl p-6 space-y-4">
          <p className="eyebrow">{id ? "Live on site" : "Draft"}</p>
          <p className="text-sm text-neutral-600 leading-relaxed">
            {id ? "Saving updates the public page immediately." : "Publishing makes this visible on the site right away."}
          </p>
          <button type="submit" disabled={saving} data-testid="admin-save-button" className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-indigo-800 text-surface text-sm font-semibold hover:bg-indigo-900 disabled:opacity-60 transition-colors">
            {saving && <Loader2 size={15} className="animate-spin" />}
            {id ? "Save changes" : "Publish"}
          </button>
          <Link to={`/admin/${kind}`} className="block text-center text-sm text-neutral-600 hover:text-neutral-900 transition-colors" data-testid="admin-cancel-button">
            Cancel
          </Link>
          {id && cfg.publicPath && (
            <a href={`${cfg.publicPath}/${data.slug}`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 text-sm font-semibold text-indigo-800 hover:text-indigo-950 pt-3 border-t border-neutral-200" data-testid="admin-view-live">
              View live page <ExternalLink size={13} />
            </a>
          )}
        </aside>
      </div>
    </form>
  );
}
